import {join, select, put} from 'redux-saga/effects';
import {matchPath} from '@computerrock/formation-router';
import * as serviceCaseActionTypes from '../serviceCaseActionTypes';
import routePaths from '../../routePaths';
import ecsAccessControl from '../../ecsAccessControl';
import {ecsFeatures, ecsFeatureActions} from '../../application/ecsFeatures';
import {startServiceCaseLockRequest, startServiceCaseUnlockRequest} from './serviceCaseLockRequests';

const isJSONString = string => {
    try {
        JSON.parse(string);
    } catch (error) {
        return false;
    }
    return true;
};

/**
 * The lock saga runs on every location change, including query-param-only changes
 * and navigation between sub-routes of the same case.
 * This is used to request the lock only once per service case.
 * Kept in memory (not sessionStorage) on purpose:
 * the case is unlocked on page unload, so after a reload the lock has to be requested again.
 */
let currentLock = null;

const isCurrentLockRequest = requestId => currentLock?.requestId === requestId;

const isActiveLock = function* isActiveLock(lock) {
    const {location} = yield select(state => state.router);
    const match = matchPath(location.pathname, {path: routePaths.SERVICE_CASE_SECTION});
    return isCurrentLockRequest(lock.requestId) && match?.params.serviceCaseId === lock.serviceCaseId;
};

// Only the cancellable route loader publishes UI updates. Detached tasks only finish HTTP requests.
const applyLockStatus = function* applyLockStatus(lock, lockResponse) {
    if (lock.isApplied || !(yield* isActiveLock(lock))) return;
    const {serviceCaseId} = lock;

    // trigger service case lock timer (30 minutes)
    if (!lockResponse.error && lockResponse.type === serviceCaseActionTypes.LOCK_SERVICE_CASE_REQUEST_SUCCEEDED) {
        const {payload} = lockResponse;
        const {lockData} = payload.response;

        yield put({
            type: serviceCaseActionTypes.START_SERVICE_CASE_LOCK_TIMER,
            payload: {serviceCaseId},
        });

        if (!(yield* isActiveLock(lock))) return;
        yield put({
            type: serviceCaseActionTypes.SET_SERVICE_CASE_LOCK_STATUS,
            payload: {lockData, serviceCaseId},
        });
        lock.isApplied = true;
        return;
    }

    // if locking fails, get lock data from the response
    const {payload} = lockResponse;
    if (isJSONString(payload.message)) {
        const lockData = JSON.parse(payload.message);
        yield put({
            type: serviceCaseActionTypes.SET_SERVICE_CASE_LOCK_STATUS,
            payload: {lockData, serviceCaseId},
        });
    }

    // A failure must not clear ownership established by a newer route loader.
    if (isCurrentLockRequest(lock.requestId)) currentLock = null;
};

/**
 * Load service case lock status
 */
const loadServiceCaseLockStatus = function* loadServiceCaseLockStatus() {
    const {serviceManager} = yield select(state => state.application);
    const ecsFlowService = serviceManager.loadService('ecsFlowService');
    const isLockManipulationAvailable = ecsAccessControl.grantFeatureAccess(
        ecsFeatures.CASE_LOCK,
        ecsFeatureActions.UPDATE,
    );

    if (!isLockManipulationAvailable) return;

    const {location} = yield select(state => state.router);

    // exit if NOT under service case section
    const serviceCaseSectionMatch = matchPath(location.pathname, {
        path: routePaths.SERVICE_CASE_SECTION,
    });

    const prevServiceCaseId = sessionStorage.getItem('serviceCaseId');
    if (!serviceCaseSectionMatch) {
        if (prevServiceCaseId) {
            yield* startServiceCaseUnlockRequest(ecsFlowService, prevServiceCaseId);
            // remove serviceCaseId from local storage
            sessionStorage.removeItem('serviceCaseId');
        }
        // Ignore pending responses after leaving; request a lock again on return.
        currentLock = null;
        return;
    }

    const {params} = serviceCaseSectionMatch;
    const {serviceCaseId} = params;

    // if agent switches from case1 to case2 and stays under service case section
    if (prevServiceCaseId && serviceCaseId !== prevServiceCaseId) {
        yield* startServiceCaseUnlockRequest(ecsFlowService, prevServiceCaseId);
    }

    sessionStorage.setItem('serviceCaseId', serviceCaseId);

    if (currentLock?.serviceCaseId !== serviceCaseId) {
        currentLock = yield* startServiceCaseLockRequest(ecsFlowService, serviceCaseId, {
            caller: 'loadServiceCaseLockStatus',
            isNavigation: true,
        });
    }
    const lock = currentLock;

    // Every route loader waits for the shared result before the remaining case loaders run.
    const lockResponse = yield join(lock.task);
    yield* applyLockStatus(lock, lockResponse);
};

export default loadServiceCaseLockStatus;
